// ============================================================
// FICHIER : boutique/toilesia-produit.js
// RÔLE : Widget « page produit » Shopify — PANNEAU LATÉRAL.
//   - Remplace le bouton « Ajouter au panier » par « Personnaliser » ;
//   - au clic : un tiroir (drawer) s'ouvre sur le côté :
//       • en haut : un champ pour téléverser une photo ;
//       • en dessous : 2 onglets d'historique
//           « Mes photos » → génération directe (style de la page)
//           « Mes œuvres » → œuvre utilisée directement (sans régénération)
//   - à la fin : l'image est intégrée à la galerie du produit, les
//     propriétés (creation_id, image_id, style, format, urls) sont
//     injectées dans le formulaire. Le bouton « Ajouter au panier »
//     réapparaît ET « Personnaliser » reste affiché : le client peut
//     commander l'œuvre en cours ou choisir une autre image.
//     (tailles/prix/cadres gérés par Shopify).
//
//   - SÉCURITÉ DE VENTE : si le widget ne peut pas s'initialiser (module
//     non chargé, session impossible / API injoignable — ex. certificat
//     TLS refusé par un navigateur mobile, « Failed to fetch »), un
//     message d'erreur est affiché À LA PLACE du bouton « Ajouter au
//     panier », le bouton est masqué et la soumission du formulaire est
//     bloquée : le client ne peut pas commander un article qu'il n'a pas
//     pu personnaliser. Voir widgetIndisponible() plus bas.
//     Désactivable : Toilesia.config.bloquerCommandeSiWidgetIndisponible.
//
// ⚠️ Le POSITIONNEMENT du panneau est embarqué dans ce fichier, mais les
//    COULEURS viennent de la palette partagée de toilesia.css (variables
//    --tls-*). Cette feuille est donc REQUISE : elle est chargée dans le
//    Shadow Root depuis l'URL fournie par data-css (recommandé :
//    data-css="{{ 'toilesia.css' | asset_url }}") ou, à défaut, résolue
//    depuis l'URL de ce script en conservant le « ?v= » (anti-cache CDN).
//
// Usage sur une page produit :
//   <span data-toilesia-produit data-style="aquarelle"></span>
//   ...scripts (config.js, auth-client.js, modules..., ce fichier)...
//   // ou via JS : ToilesiaProduit.init({ style: 'aquarelle' });
// ============================================================

window.ToilesiaProduit = (function () {
    'use strict';

    // document.currentScript n'est valide que pendant l'exécution synchrone
    const _scriptCourant = document.currentScript;

    // ============================================================
    // CSS EMBARQUÉ DU PANNEAU (positionnement uniquement)
    // Les COULEURS viennent de la palette partagée de toilesia.css
    // (variables --tls-*), feuille chargée dans ce même Shadow Root
    // — voir chargerCSS() plus bas.
    // ============================================================
    const CSS_DRAWER = [
        '.tlsd,.tlsd *{box-sizing:border-box}',
        '.tlsd{position:fixed;inset:0;z-index:2147483001;visibility:hidden;pointer-events:none}',
        '.tlsd.tlsd-ouvert{visibility:visible;pointer-events:auto}',
        '.tlsd-fond{position:absolute;inset:0;background:rgba(var(--tls-ombre-rgb), 0.45);opacity:0;transition:opacity .3s ease}',
        '.tlsd.tlsd-ouvert .tlsd-fond{opacity:1}',
        '.tlsd-panneau{position:absolute;top:0;right:0;bottom:0;width:100%;max-width:850px;min-width:0;',
        ' background:var(--tls-fond);color:var(--tls-encre);box-shadow:-14px 0 44px rgba(var(--tls-ombre-rgb), 0.28);',
        ' transform:translateX(102%);transition:transform .34s cubic-bezier(.22,.8,.3,1);',
        ' display:flex;flex-direction:column}',
        '.tlsd.tlsd-ouvert .tlsd-panneau{transform:translateX(0)}',
        '@media(max-width:850px){.tlsd-panneau{max-width:none}}',
        '.tlsd-entete{display:flex;align-items:center;justify-content:space-between;gap:12px;',
        ' padding:16px 18px;background:var(--tls-surface);border-bottom:1px solid var(--tls-bordure);flex-shrink:0}',
        '.tlsd-titre{font-family:cursive,"Times New Roman",serif;font-size:1.65rem;margin:0;color:var(--tls-encre);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
        '.tlsd-fermer{width:34px;height:34px;border:none;border-radius:50%;background:var(--tls-fond);',
        ' color:var(--tls-encre);font-size:1rem;cursor:pointer;flex-shrink:0;transition:background .2s}',
        '.tlsd-fermer:hover{background:var(--tls-accent-clair)}',
        '.tlsd-corps{flex:1;min-width:0;width:100%;overflow-y:auto;overflow-x:hidden;padding:16px 18px 22px;',
        ' display:flex;flex-direction:column;gap:14px}',
        '.tlsd-sous-titre{font-size:.88rem;color:var(--tls-texte-muet);margin:0;line-height:1.45}',
        '.tlsd-upload{background:var(--tls-surface);border:1px solid var(--tls-bordure);border-radius:14px;padding:4px;min-width:0;max-width:100%}',
        '.tlsd-upload .tls-drop{padding:14px 10px;border-radius:10px}',
        '.tlsd-upload .tls-drop-icone{font-size:26px;margin-bottom:4px}',
        '.tlsd-upload .tls-drop-texte{font-size:.85rem;margin-bottom:8px}',
        '.tlsd-upload .tls-drop-info{font-size:.72rem}',
        '.tlsd-upload .tls-upload-apercu img{display:block;width:auto;max-width:100%;height:auto;max-height:280px;object-fit:contain;margin:0 auto}',
        '.tlsd-tabs{display:flex;gap:4px;border-bottom:1px solid var(--tls-bordure)}',
        '.tlsd-onglet{flex:1;padding:10px 8px;border:none;background:none;font-family:inherit;',
        ' font-size:.92rem;font-weight:600;color:var(--tls-texte-muet);cursor:pointer;',
        ' border-bottom:2.5px solid transparent;transition:color .2s,border-color .2s}',
        '.tlsd-onglet.tlsd-actif{color:var(--tls-accent-fonce);border-bottom-color:var(--tls-accent)}',
        '.tlsd-hint{font-size:.78rem;color:var(--tls-texte-muet);margin:0}',
        '.tlsd-cadre{position:relative;flex:1;display:flex;flex-direction:column;overflow:hidden}',
        '.tlsd-grille{display:flex;gap:10px;padding-bottom:8px;',
        ' scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch}',
        '.tlsd-grille::-webkit-scrollbar{height:6px}',
        '.tlsd-grille::-webkit-scrollbar-thumb{background:var(--tls-bordure-accent);border-radius:999px}',
        '.tlsd-carte{position:relative;border:2px solid transparent;border-radius:10px;overflow:hidden;',
        ' padding:0;background:var(--tls-surface);cursor:pointer;width:104px;height:140px;flex:0 0 auto;',
        ' scroll-snap-align:start;transition:border-color .15s,transform .15s,box-shadow .15s}',
        '.tlsd-carte:hover{transform:translateY(-2px);border-color:var(--tls-accent);',
        ' box-shadow:0 6px 16px rgba(var(--tls-ombre-rgb), 0.12)}',
        '.tlsd-carte img{width:100%;height:100%;object-fit:cover;display:block}',
        '.tlsd-carte-info{position:absolute;left:0;right:0;bottom:0;padding:3px 6px;',
        ' background:rgba(var(--tls-ombre-rgb), 0.72);color:var(--tls-fond);font-size:.66rem;',
        ' white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:left}',
        '.tlsd-overlay{position:absolute;inset:0;z-index:6;background:rgba(var(--tls-fond-rgb), 0.97);',
        ' display:none;flex-direction:column;justify-content:center;padding:20px;overflow-y:auto;max-width:100%}',
        '.tlsd-overlay.tlsd-visible{display:flex}',
        '.tlsd-consentement{background:var(--tls-surface);border:1px solid var(--tls-bordure);border-radius:14px;padding:20px;max-width:440px;margin:0 auto;text-align:left}',
        '.tlsd-consentement-titre{margin:0 0 12px;font-size:1.05rem}',
        '.tlsd-consentement-ligne{display:flex;align-items:flex-start;gap:8px;font-size:.9rem;margin:0 0 10px;cursor:pointer}',
        '.tlsd-consentement-ligne input[type="checkbox"]{width:18px;height:18px;margin-top:2px;flex:0 0 auto;accent-color:var(--tls-accent)}',
        '.tlsd-consentement-actions{display:flex;gap:10px;margin-top:16px}',
        '.tlsd-consentement-actions button{flex:1}',
        '@media(prefers-reduced-motion:reduce){.tlsd-panneau,.tlsd-fond{transition:none}}'
    ].join('');

    // Notifications : le style vient entièrement de toilesia.css
    // (palette partagée). Seul écart nécessaire ici : les toasts doivent
    // passer AU-DESSUS du panneau du tiroir (z-index 2147483001, alors
    // que toilesia.css prévoit 2147483000 pour le widget).
    const CSS_NOTIFS = ['.tls-toasts.tls-toasts{z-index:2147483002}'].join('');

    // Message « personnalisation indisponible » affiché DANS LA PAGE
    // (remplace le bouton « Ajouter au panier »).
    // ⚠️ toilesia.css n'est chargée que dans le Shadow Root du tiroir :
    //    elle ne peut pas styler ce bloc → les styles sont EMBARQUÉS ici,
    //    avec les mêmes valeurs que la palette partagée de toilesia.css
    //    (--tls-erreur-clair #e05a4a, --tls-erreur-fond #fdebea,
    //     --tls-erreur-texte #8e2b1f, --tls-accent-fonce #2f35a8).
    const CSS_PAGE = [
        '.tls-p-erreur{display:flex;gap:12px;align-items:flex-start;margin:0 0 14px;padding:14px 16px;',
        ' border:1px solid #e05a4a;border-left-width:4px;border-radius:10px;background:#fdebea;',
        ' color:#8e2b1f;font-family:inherit;font-size:.92rem;line-height:1.45;text-align:left}',
        '.tls-p-erreur-icone{flex:0 0 auto;font-size:1.15rem;line-height:1.2}',
        '.tls-p-erreur-titre{display:block;margin:0 0 4px;font-size:.98rem;font-weight:700}',
        '.tls-p-erreur p{margin:0 0 10px}',
        '.tls-p-erreur button{padding:9px 16px;border:none;border-radius:8px;background:#2f35a8;color:#fff;',
        ' font-family:inherit;font-size:.85rem;font-weight:600;cursor:pointer}',
        '.tls-p-erreur button[disabled]{opacity:.6;cursor:progress}',
        // ---- Masquage des blocs d'achat du THÈME non pilotés par le JS ----
        // ⚠️ Ces règles étaient jusqu'ici dans toilesia.css, chargée
        //    UNIQUEMENT dans le Shadow Root du tiroir → elles n'avaient
        //    AUCUN effet sur la page (barre « sticky » mobile, paiement
        //    accéléré). Elles sont injectées ici, dans <head>, et activées
        //    par la classe .tls-p-verrou posée sur <html> tant que la
        //    personnalisation n'est ni prête ni terminée (voir
        //    poserVerrouAjoutPanier / leverVerrouAjoutPanier).
        'html.tls-p-verrou .sticky-add-to-cart,',
        'html.tls-p-verrou .accelerated-checkout-block,',
        'html.tls-p-verrou .shopify-payment-button,',
        'html.tls-p-verrou [data-shopify="payment-button"]{display:none!important}',
        // ---- Repère de chargement de l'œuvre (galerie produit) ----
        // Les images personnalisées sont chargées en blob depuis l'API : sur
        // une connexion lente, elles restent invisibles plusieurs secondes.
        '.tls-p-chargement{background:linear-gradient(100deg,#d5dbe8 40%,#f2f3fa 50%,#d5dbe8 60%);',
        ' background-size:200% 100%;animation:tls-p-shimmer 1.4s linear infinite}',
        '@keyframes tls-p-shimmer{to{background-position:-200% 0}}',
        '@media(prefers-reduced-motion:reduce){.tls-p-chargement{animation:none;background:#e8ebfb}}'
    ].join('');

    // ---- État ----
    let _style = '';
    let _form = null;              // formulaire produit Shopify
    let _btnAjout = null;          // bouton « Ajouter au panier » original
    let _boutonsAjout = [];        // TOUS les boutons « Ajouter au panier » (principal + mobile/sticky)
    let _btnPerso = null;          // notre bouton « Personnaliser »
    let _hostNotif = null;         // hôte des notifications (persistant)
    let _hostDrawer = null;        // hôte du tiroir
    let _shadow = null;            // Shadow Root du tiroir
    let _drawerOuvert = false;
    let _initialise = false;       // anti double initialisation
    let _tentativeInit = 0;        // réessais si les modules chargent en async
    let _urlOeuvre = '';           // dernière œuvre affichée dans la galerie (URL blob)
    let _dernierCreation = null;   // dernière œuvre finalisée (réinjection des propriétés)
    let _rafSurveillance = 0;      // debounce (requestAnimationFrame) après mutation galerie
    let _observateurGalerie = null; // MutationObserver : réaffiche l'œuvre après reset variante
    let _cssUrl = '';              // URL du CSS partagé (data-css / config.cssUrl)
    let _bloque = false;           // ajout au panier bloqué (widget indisponible)
    let _blocErreur = null;        // message « personnalisation indisponible » (page)
    let _cssPageInjectee = false;  // styles du message déjà injectés dans le <head>
    let _soumissionSurveillee = false; // garde posée sur la soumission du formulaire
    let _observateurBouton = null; // MutationObserver : re-masque le bouton recréé par le thème

    // ---- Sélecteurs (adaptables au thème) ----
    const SEL_FORM = 'form[action*="/cart/add"], [data-product-form], #product_form, form.product-form, form#AddToCartForm';
    const SEL_BTN = 'button[type="submit"], [data-add-to-cart], .product-form__submit, button[name="add"], #AddToCart';
    // Marqueurs explicites « ajouter au panier » utilisés pour la recherche
    // HORS formulaire (barre sticky mobile, doublons du thème). On évite le
    // sélecteur générique button[type=submit] hors formulaire pour ne pas
    // masquer d'autres boutons de la page (newsletter, recherche…).
    const SEL_BTN_AJOUT = '[data-add-to-cart], .product-form__submit, button[name="add"], #AddToCart, ' +
        '[data-sticky-add-to-cart] button, [data-sticky-add-to-cart] a, ' +
        '.sticky-add-to-cart, .sticky-add-to-cart button, .sticky-add-to-cart a, ' +
        '.shopify-payment-button, [data-shopify="payment-button"] button';

    // ============================================================
    // INITIALISATION
    // ============================================================
    async function init(opts) {
        opts = opts || {};
        if (_initialise) return;

        // 0. VERROU IMMÉDIAT de l'ajout au panier — AVANT toute attente
        //    réseau (création de session) ET avant la vérification des
        //    modules (qui peut réessayer ~1,5 s). Idempotent : le verrou est
        //    déjà posé au chargement du script (auto-init en fin de fichier),
        //    il est simplement réaffirmé ici pour un init() manuel.
        poserVerrouAjoutPanier();

        // Vérification des modules requis — certains scripts peuvent
        // être chargés en async (ordre non garanti) : on réessaie.
        if (!verifierModules()) {
            if (_tentativeInit < 6) {
                _tentativeInit++;
                setTimeout(() => init(opts), 250);
            } else {
                console.error(
                    '[ToilesiaProduit] Modules requis manquants :',
                    verifierModulesManquants(),
                    '— le bouton « Personnaliser » n\'est pas activé.'
                );
                // Sans module, aucune personnalisation n'est possible : on
                // prévient le client et on bloque l'ajout au panier.
                widgetIndisponible('modules');
            }
            return;
        }
        _initialise = true;

        if (opts.apiUrl) Toilesia.config.apiUrl = opts.apiUrl;
        _style = opts.style || '';

        // data-style éventuel sur un conteneur de la page
        const conteneur = document.querySelector('[data-toilesia-produit]');
        if (conteneur && conteneur.getAttribute('data-style')) {
            _style = conteneur.getAttribute('data-style');
        }
        if (!_style) _style = Toilesia.config.style;

        // URL de la feuille de style partagée : data-css (recommandé sur
        // Shopify : {{ 'toilesia.css' | asset_url }}, donc versionnée),
        // sinon config.cssUrl, sinon repli depuis l'URL de ce script.
        if (conteneur && conteneur.getAttribute('data-css')) {
            _cssUrl = conteneur.getAttribute('data-css');
        } else if (Toilesia.config.cssUrl) {
            _cssUrl = Toilesia.config.cssUrl;
        }

        // On n'agit que si une vraie page produit est détectée
        if (!document.querySelector(SEL_FORM) && !document.querySelector(SEL_BTN)) {
            console.warn('[ToilesiaProduit] Aucune page produit détectée — widget inactif.');
            // Aucune personnalisation possible ici : on retire le verrou
            // (sécurité, au cas où la page aurait été détectée à tort).
            leverVerrouAjoutPanier();
            return;
        }

        // (Le verrou de l'ajout au panier est déjà ACTIF — posé au début de
        //  init() et dès le chargement du script : le bouton du thème est
        //  masqué pendant toute la création de session ci-dessous.)

        try {
            await Toilesia.session.garantir();
        } catch (e) {
            console.error('[ToilesiaProduit] Impossible de créer la session :', e);
            // API injoignable (réseau, certificat…) : aucune
            // personnalisation possible → message + vente bloquée.
            widgetIndisponible('session');
            return;
        }

        if (!trouverFormulaireEtBouton()) return;
        // Le blocage posé avant la création de session reste ACTIF : on
        // retire seulement un éventuel message d'erreur d'une tentative
        // précédente, puis on installe le bouton « Personnaliser ».
        retirerBlocErreur();
        remplacerBouton();
    }

    /**
     * Vérifie que tous les modules nécessaires sont chargés.
     * @returns {boolean}
     */
    function verifierModules() {
        return !!(
            window.Toilesia && Toilesia.config &&
            window.ToilesiaAuth && ToilesiaAuth.apiCall &&
            Toilesia.session && Toilesia.api &&
            Toilesia.notifier && Toilesia.progress && Toilesia.upload
        );
    }

    /** Liste les fichiers manquants (pour le diagnostic console). */
    function verifierModulesManquants() {
        const manquants = [];
        if (!window.Toilesia || !Toilesia.config) manquants.push('config.js');
        if (!window.ToilesiaAuth) manquants.push('auth-client.js');
        if (!Toilesia || !Toilesia.session) manquants.push('modules/session.js');
        if (!Toilesia || !Toilesia.api) manquants.push('modules/api.js');
        if (!Toilesia || !Toilesia.notifier) manquants.push('modules/notifications.js');
        if (!Toilesia || !Toilesia.progress) manquants.push('modules/progress.js');
        if (!Toilesia || !Toilesia.upload) manquants.push('modules/upload.js');
        return manquants.length ? manquants.join(', ') : 'aucun';
    }

    function trouverFormulaireEtBouton() {
        _form = document.querySelector(SEL_FORM);
        rafraichirBoutonsAjout(); // renseigne _btnAjout (principal) + _boutonsAjout (tous)
        if (!_btnAjout) {
            console.warn('[ToilesiaProduit] Bouton « Ajouter au panier » introuvable.');
            return false;
        }
        if (!_form) _form = _btnAjout.closest('form');
        return true;
    }

    function remplacerBouton() {
        // Nouvelle tentative après une indisponibilité : un bouton
        // « Personnaliser » déjà en place ne doit pas être dupliqué.
        if (_btnPerso && _btnPerso.isConnected) {
            definirVisibiliteTousBoutons(false);
            return;
        }

        _btnPerso = document.createElement('button');
        _btnPerso.type = 'button';
        // On reprend les classes du bouton original → même rendu que le thème
        _btnPerso.className = _btnAjout.className + ' tls-p-perso';
        _btnPerso.textContent = '🎨 Personnaliser';
        _btnPerso.setAttribute('aria-label', 'Personnaliser le produit avec votre photo');
        // Phase de CAPTURE : insensible aux éventuels stopPropagation du thème
        _btnPerso.addEventListener('click', ouvrirDrawer, true);

        definirVisibiliteTousBoutons(false);
        _btnAjout.parentNode.insertBefore(_btnPerso, _btnAjout.nextSibling);
    }

    // ============================================================
    // INDISPONIBILITÉ DU WIDGET (module non chargé, session impossible)
    // ------------------------------------------------------------
    // Règle : un client ne doit pas pouvoir commander un article qu'il
    // n'a pas pu personnaliser. On affiche donc un message d'erreur À LA
    // PLACE du bouton « Ajouter au panier », on masque ce bouton et on
    // bloque la soumission du formulaire (touche Entrée, thèmes AJAX…).
    // Désactivable : Toilesia.config.bloquerCommandeSiWidgetIndisponible
    // ============================================================

    /** Masque (display:none !important) ou rétablit un bouton du thème. */
    function definirVisibiliteBouton(btn, visible) {
        if (!btn) return;
        if (visible) {
            btn.style.removeProperty('display');
        } else {
            btn.style.setProperty('display', 'none', 'important');
        }
    }

    /**
     * Liste TOUS les boutons « Ajouter au panier » de la page : celui du
     * formulaire principal + les doublons du thème (barre sticky mobile,
     * bouton en bas de page…). Met à jour _btnAjout (principal) et
     * _boutonsAjout (ensemble), dédoublonnés.
     * @returns {Array<HTMLElement>}
     */
    function rafraichirBoutonsAjout() {
        const candidats = [];
        if (_form) {
            Array.prototype.push.apply(candidats, _form.querySelectorAll(SEL_BTN));
        }
        Array.prototype.push.apply(candidats, document.querySelectorAll(SEL_BTN));

        // Filtre : dans le formulaire produit, tout bouton correspondant à
        // SEL_BTN est retenu ; HORS formulaire, seuls les marqueurs
        // explicites « ajouter au panier » sont retenus (pour ne pas masquer
        // un submit de newsletter, de recherche…).
        const dansForm = new Set(_form ? _form.querySelectorAll(SEL_BTN) : []);
        const vus = new Set();
        const tous = [];
        candidats.forEach((b) => {
            // On exclut notre propre bouton « Personnaliser » (il reprend
            // les classes du bouton original et pourrait être confondu).
            if (!b || !b.isConnected || b === _btnPerso || vus.has(b)) return;
            if (!dansForm.has(b) && !b.matches(SEL_BTN_AJOUT)) return;
            vus.add(b);
            tous.push(b);
        });

        _boutonsAjout = tous;
        // Le bouton principal est celui du formulaire (ou le 1er trouvé)
        if (!_btnAjout || !_btnAjout.isConnected || !tous.includes(_btnAjout)) {
            _btnAjout = tous[0] || null;
        }
        return tous;
    }

    /** Masque ou rétablit TOUS les boutons d'ajout au panier (dont mobile/sticky). */
    function definirVisibiliteTousBoutons(visible) {
        rafraichirBoutonsAjout();
        _boutonsAjout.forEach((btn) => {
            definirVisibiliteBouton(btn, visible);
            if (visible) {
                btn.removeAttribute('disabled');
                btn.removeAttribute('aria-hidden');
            } else {
                btn.setAttribute('disabled', 'disabled');
                btn.setAttribute('aria-hidden', 'true');
            }
        });
    }

    /**
     * Le widget ne peut pas fonctionner : message d'erreur visible et,
     * selon la configuration, blocage de l'ajout au panier.
     * @param {string} raison 'session' | 'modules' (diagnostic console)
     */
    function widgetIndisponible(raison) {
        const cfg = (window.Toilesia && Toilesia.config) || {};
        const bloquer = cfg.bloquerCommandeSiWidgetIndisponible !== false;

        if (!_btnAjout) trouverFormulaireEtBouton();

        if (bloquer) {
            poserVerrouAjoutPanier();
        } else {
            // L'option bloquerCommandeSiWidgetIndisponible = false laisse la
            // boutique vendable : on lève le blocage posé plus tôt (pendant
            // la création de session) pour ne pas entraver la commande.
            retablirAjoutPanier();
        }

        afficherBlocErreur(bloquer);

        console.error(
            '[ToilesiaProduit] Personnalisation indisponible (' + raison + ') — ' +
            (bloquer
                ? 'ajout au panier bloqué sur cette page produit.'
                : 'ajout au panier laissé ouvert (bloquerCommandeSiWidgetIndisponible = false).')
        );
    }

    /** Retire le message et libère l'ajout au panier. */
    function retablirAjoutPanier() {
        if (!_bloque && !_blocErreur) return;

        leverVerrouAjoutPanier();
        retirerBlocErreur();
        definirVisibiliteTousBoutons(true);
        console.info('[ToilesiaProduit] Personnalisation rétablie : ajout au panier libéré.');
    }

    /** La page courante est-elle une page produit ? (marqueur ou formulaire) */
    function estPageProduit() {
        return !!(
            document.querySelector('[data-toilesia-produit]') ||
            document.querySelector(SEL_FORM) ||
            document.querySelector(SEL_BTN)
        );
    }

    /**
     * VERROU IMMÉDIAT de l'ajout au panier.
     *
     * ⚠️ Appelée DÈS LE CHARGEMENT du script (voir l'auto-init en fin de
     *    fichier), et non à la création de la session : sur une connexion
     *    lente, `session/new` peut prendre plusieurs secondes et le client ne
     *    doit ni pouvoir commander un produit non personnalisé, NI voir le
     *    bouton du thème rester affiché pendant ce délai (« il n'est remplacé
     *    que quelques secondes après »).
     *    Idempotente : appelable plusieurs fois sans effet de bord.
     */
    function poserVerrouAjoutPanier() {
        if (!estPageProduit()) return; // jamais de verrou hors page produit

        _bloque = true;
        injecterCSSPage();
        if (document.documentElement) {
            document.documentElement.classList.add('tls-p-verrou');
        }

        rafraichirBoutonsAjout();
        _boutonsAjout.forEach((btn) => {
            definirVisibiliteBouton(btn, false);
            btn.setAttribute('disabled', 'disabled');
            btn.setAttribute('aria-hidden', 'true');
        });

        // Re-masquage continu (bouton recréé par le thème, barre sticky
        // rendue plus tard…) + blocage de la soumission du formulaire.
        surveillerAjoutPanier();
        surveillerSoumission();
    }

    /**
     * Lève le verrou : la vente redevient possible (œuvre prête, ou repli
     * bloquerCommandeSiWidgetIndisponible = false).
     */
    function leverVerrouAjoutPanier() {
        _bloque = false;
        if (document.documentElement) {
            document.documentElement.classList.remove('tls-p-verrou');
        }
        if (_observateurBouton) {
            _observateurBouton.disconnect();
            _observateurBouton = null;
        }
    }

    /**
     * Le thème peut recréer le bouton (changement de variante) ou en ajouter
     * un autre (barre sticky mobile, paiement accéléré) : on le re-masque
     * aussitôt. Surveillance sur tout le document pour couvrir ces cas.
     *
     * ⚠️ La surveillance est armée MÊME SI aucun bouton n'existe encore :
     *    sinon un bouton rendu après coup (thème asynchrone, connexion lente)
     *    resterait visible alors que le verrou est actif.
     */
    function surveillerAjoutPanier() {
        if (_observateurBouton || typeof MutationObserver === 'undefined') return;
        const cible = document.body || document.documentElement;
        if (!cible) return;
        _observateurBouton = new MutationObserver(() => {
            if (!_bloque) return;
            rafraichirBoutonsAjout();
            _boutonsAjout.forEach((btn) => {
                if (btn.style.display === 'none') return; // déjà masqué
                definirVisibiliteBouton(btn, false);
                btn.setAttribute('disabled', 'disabled');
                btn.setAttribute('aria-hidden', 'true');
            });
        });
        _observateurBouton.observe(cible, { childList: true, subtree: true });
    }

    /**
     * Garde posée une seule fois, en phase de CAPTURE : tant que le
     * blocage est actif, la soumission du formulaire produit (clic AJAX,
     * touche Entrée, handler du thème) est annulée avant tout traitement.
     */
    function surveillerSoumission() {
        if (_soumissionSurveillee) return;
        _soumissionSurveillee = true;

        document.addEventListener('submit', (e) => {
            if (!_bloque) return;
            const cible = e.target;
            if (!cible || !cible.matches || !cible.matches(SEL_FORM)) return;
            e.preventDefault();
            e.stopPropagation();
            console.warn('[ToilesiaProduit] Ajout au panier bloqué : la personnalisation est indisponible.');
        }, true);
    }

    /** Injecte les styles du message (une seule fois) dans le <head>. */
    function injecterCSSPage() {
        if (_cssPageInjectee || !document.head) return;
        _cssPageInjectee = true;
        const style = document.createElement('style');
        style.id = 'tls-produit-indisponible';
        style.textContent = CSS_PAGE;
        document.head.appendChild(style);
    }

    /**
     * Affiche le message d'erreur à la place du bouton « Ajouter au
     * panier » (idempotent : un seul bloc à la fois).
     * @param {boolean} bloquer Texte adapté selon que la vente est bloquée
     */
    function afficherBlocErreur(bloquer) {
        if (_blocErreur && _blocErreur.isConnected) return;
        injecterCSSPage();

        const bloc = document.createElement('div');
        bloc.className = 'tls-p-erreur';
        bloc.setAttribute('role', 'alert');

        const icone = document.createElement('span');
        icone.className = 'tls-p-erreur-icone';
        icone.textContent = '⚠';

        const corps = document.createElement('div');

        const titre = document.createElement('strong');
        titre.className = 'tls-p-erreur-titre';
        titre.textContent = 'Personnalisation momentanément indisponible';

        const texte = document.createElement('p');
        texte.textContent = bloquer
            ? 'Le service de personnalisation est injoignable : ce produit ne peut pas être ajouté au panier. ' +
              'Vérifiez votre connexion, puis cliquez sur « Réessayer ».'
            : 'Le service de personnalisation est injoignable. Vous pouvez tout de même ajouter ce produit au panier.';

        const btnReessayer = document.createElement('button');
        btnReessayer.type = 'button';
        btnReessayer.textContent = 'Réessayer';
        btnReessayer.addEventListener('click', () => {
            btnReessayer.disabled = true;
            btnReessayer.textContent = 'Nouvelle tentative…';
            reessayer();
        });

        corps.appendChild(titre);
        corps.appendChild(texte);
        corps.appendChild(btnReessayer);
        bloc.appendChild(icone);
        bloc.appendChild(corps);

        _blocErreur = bloc;
        if (_btnAjout && _btnAjout.parentNode) {
            // Juste avant le bouton masqué : le message prend sa place.
            _btnAjout.parentNode.insertBefore(bloc, _btnAjout);
        } else if (_form) {
            _form.appendChild(bloc);
        } else {
            document.body.appendChild(bloc);
        }
    }

    function retirerBlocErreur() {
        if (_blocErreur && _blocErreur.parentNode) {
            _blocErreur.parentNode.removeChild(_blocErreur);
        }
        _blocErreur = null;
    }

    /** Bouton « Réessayer » : relance l'initialisation complète du widget. */
    function reessayer() {
        console.info('[ToilesiaProduit] Nouvelle tentative d\'initialisation du widget…');
        _initialise = false;
        _tentativeInit = 0;
        init({ style: _style });
    }

    // ============================================================
    // TIROIR LATÉRAL (drawer)
    // ============================================================
    function creerHoteNotif() {
        if (!Toilesia.notifier) return;
        _hostNotif = document.createElement('div');
        _hostNotif.id = 'tls-produit-notifications';
        document.body.appendChild(_hostNotif);
        const racine = _hostNotif.attachShadow({ mode: 'open' });
        const style = document.createElement('style');
        style.textContent = CSS_NOTIFS;
        racine.appendChild(style);
        // Palette partagée (couleurs des toasts)
        chargerCSS(racine, 'toilesia.css');
        Toilesia.notifier.initialiser(racine);
    }

    function creerHoteDrawer() {
        _hostDrawer = document.createElement('div');
        _hostDrawer.id = 'tls-produit-drawer';
        document.body.appendChild(_hostDrawer);
        _shadow = _hostDrawer.attachShadow({ mode: 'open' });

        // 1. CSS embarqué (positionnement du tiroir TOUJOURS garanti)
        const style = document.createElement('style');
        style.textContent = CSS_DRAWER;
        _shadow.appendChild(style);

        // 2. toilesia.css pour les composants partagés (upload, progression)
        chargerCSS(_shadow, 'toilesia.css');

        // 3. DOM du tiroir
        _shadow.innerHTML += construireDomDrawer();

        // 4. Événements
        _shadow.querySelector('.tlsd-fermer').addEventListener('click', fermerDrawer);
        _shadow.querySelector('.tlsd-fond').addEventListener('click', fermerDrawer);
        _shadow.querySelectorAll('.tlsd-onglet').forEach((onglet) => {
            onglet.addEventListener('click', () => chargerOnglet(onglet.dataset.onglet));
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && _drawerOuvert) fermerDrawer();
        });

        // 5. Champ d'upload en partie haute
        initialiserUpload();
    }

    function construireDomDrawer() {
        return '' +
            '<div class="tlsd">' +
            '  <div class="tlsd-fond" data-role="fond"></div>' +
            '  <aside class="tlsd-panneau" role="dialog" aria-modal="true" aria-label="Personnaliser votre produit">' +
            '    <header class="tlsd-entete">' +
            '      <h3 class="tlsd-titre">Personnalisez votre produit</h3>' +
            '      <button type="button" class="tlsd-fermer" aria-label="Fermer">✕</button>' +
            '    </header>' +
            '    <div class="tlsd-cadre">' +
            '      <div class="tlsd-corps">' +
            '        <p class="tlsd-sous-titre">Téléversez une photo de votre animal : ' +
            'notre IA crée une œuvre « <strong data-role="style-nom"></strong> ».</p>' +
            '        <div class="tlsd-upload" data-role="upload"></div>' +
            '        <div class="tlsd-tabs" role="tablist">' +
            '          <button type="button" class="tlsd-onglet tlsd-actif" data-onglet="photos" role="tab" aria-selected="true">Mes photos</button>' +
            '          <button type="button" class="tlsd-onglet" data-onglet="oeuvres" role="tab" aria-selected="false">Mes œuvres</button>' +
            '        </div>' +
            '        <p class="tlsd-hint" data-role="hint"></p>' +
            '        <div class="tlsd-grille" data-role="grille"></div>' +
            '      </div>' +
            '      <div class="tlsd-overlay" data-role="overlay"></div>' +
            '    </div>' +
            '  </aside>' +
            '</div>';
    }

    /**
     * Résout l'URL de la feuille de style partagée.
     *  1) data-css (ou config.cssUrl) : URL VERSIONNÉE par Shopify
     *     ({{ 'toilesia.css' | asset_url }} → …?v=<hash>). Indispensable :
     *     le CDN sert ces fichiers avec un cache très long ; sans ce « v »,
     *     une ancienne version peut être servie indéfiniment, même après
     *     nettoyage du cache du navigateur.
     *  2) repli : même dossier que ce script, en CONSERVANT le « ?v= ».
     * @param {string} nomFichier Ex. 'toilesia.css'
     * @returns {string} URL à charger
     */
    function urlCSS(nomFichier) {
        if (_cssUrl) return _cssUrl;

        if (_scriptCourant && _scriptCourant.src) {
            // Repli : même dossier que ce script, en CONSERVANT la query
            // (« ?v=… »), qui sert de clé de cache au CDN.
            const src = _scriptCourant.src;
            const i = src.indexOf('?');
            const chemin = (i === -1) ? src : src.slice(0, i);
            return chemin.replace(/[^/]*$/, nomFichier) + (i === -1 ? '' : src.slice(i));
        }

        console.error(
            '[ToilesiaProduit] URL du CSS introuvable : renseignez data-css ' +
            '(\'toilesia.css\' | asset_url) ou Toilesia.config.cssUrl.'
        );
        return nomFichier;
    }

    /** Injecte une feuille de style dans le Shadow Root. */
    function chargerCSS(racine, nomFichier) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = urlCSS(nomFichier);
        link.addEventListener('error', () => {
            console.error('[ToilesiaProduit] Feuille de style non chargée :', link.href);
        });
        racine.appendChild(link);
    }

    // ------------------------------------------------------------
    // Ouverture / fermeture
    // ------------------------------------------------------------
    async function ouvrirDrawer() {
        // La session a pu être perdue (ou l'API redevenir injoignable)
        // depuis l'initialisation : on la (re)garantit AVANT d'ouvrir le
        // panneau, sinon le client se retrouve dans un tiroir en erreur.
        try {
            await Toilesia.session.garantir();
        } catch (e) {
            console.error('[ToilesiaProduit] Session indisponible — tiroir non ouvert :', e);
            widgetIndisponible('session');
            return;
        }

        // Session OK : on (ré)établit l'état « en attente de
        // personnalisation » — boutons d'ajout masqués, « Personnaliser »
        // présent — sans lever le blocage (la vente reste verrouillée tant
        // qu'aucune œuvre n'a été générée).
        if (!_btnAjout) trouverFormulaireEtBouton();
        if (_btnAjout) {
            if (_btnPerso && _btnPerso.isConnected) {
                definirVisibiliteTousBoutons(false);
            } else {
                remplacerBouton();
            }
        }

        try {
            if (!_hostDrawer) creerHoteDrawer();
            if (!_hostNotif) creerHoteNotif();

            _shadow.querySelector('[data-role="style-nom"]').textContent = nomStyle(_style);
            _shadow.querySelector('.tlsd').classList.add('tlsd-ouvert');
            _drawerOuvert = true;
            document.body.style.overflow = 'hidden';
            // Réinitialisation à chaque ouverture : masque un éventuel
            // écran de progression résiduel et repart sur un upload vierge.
            montrerVueCorps();
            initialiserUpload();
            chargerOnglet('photos');
        } catch (e) {
            console.error("[ToilesiaProduit] Erreur à l'ouverture du panneau :", e);
        }
    }

    function fermerDrawer() {
        if (!_drawerOuvert) return;
        _drawerOuvert = false;
        _shadow.querySelector('.tlsd').classList.remove('tlsd-ouvert');
        document.body.style.overflow = '';
    }

    // ------------------------------------------------------------
    // Onglets « Mes photos » / « Mes œuvres »
    // ------------------------------------------------------------
    function chargerOnglet(nom) {
        _shadow.querySelectorAll('.tlsd-onglet').forEach((o) => {
            const actif = o.dataset.onglet === nom;
            o.classList.toggle('tlsd-actif', actif);
            o.setAttribute('aria-selected', actif ? 'true' : 'false');
        });

        const hint = _shadow.querySelector('[data-role="hint"]');
        hint.textContent = nom === 'photos'
            ? 'Cliquez sur une photo, validez les consentements puis lancez la génération de votre œuvre « ' + nomStyle(_style) + ' ».'
            : 'Cliquez sur une œuvre : elle est utilisée directement, sans nouvelle génération.';

        if (nom === 'photos') chargerPhotos();
        else chargerOeuvres();
    }

    async function chargerPhotos() {
        const grille = _shadow.querySelector('[data-role="grille"]');
        grille.innerHTML = '<p class="tls-vide">Chargement de vos photos…</p>';

        const reponse = await Toilesia.api.appeler('session/history', 'GET', null, { silencieux: true });
        if (!reponse.succes || !Array.isArray(reponse.historique) || !reponse.historique.length) {
            grille.innerHTML =
                '<p class="tls-vide">Aucune ancienne photo pour le moment. ' +
                'Utilisez le champ ci-dessus pour en téléverser une.</p>';
            return;
        }

        grille.innerHTML = '';
        const grid = document.createElement('div');
        grid.className = 'tlsd-grille';
        reponse.historique.forEach((item) => {
            grid.appendChild(creerCarte(item, () => demanderConsentement(item.image_id)));
        });
        grille.appendChild(grid);
    }

    async function chargerOeuvres() {
        const grille = _shadow.querySelector('[data-role="grille"]');
        grille.innerHTML = '<p class="tls-vide">Chargement de vos œuvres…</p>';

        const reponse = await Toilesia.api.appeler('session/history', 'GET', null, { silencieux: true });
        if (!reponse.succes) {
            grille.innerHTML = '<p class="tls-vide">Impossible de charger vos œuvres.</p>';
            return;
        }

        const oeuvres = [];
        (reponse.historique || []).forEach((item) => {
            (item.creations || []).forEach((creation) => {
                oeuvres.push({ item: item, creation: creation });
            });
        });

        if (!oeuvres.length) {
            grille.innerHTML =
                '<p class="tls-vide">Aucune œuvre générée pour le moment. ' +
                'Téléversez une photo ci-dessus pour créer la première.</p>';
            return;
        }

        grille.innerHTML = '';
        const grid = document.createElement('div');
        grid.className = 'tlsd-grille';
        oeuvres.forEach(({ item, creation }) => {
            const oeuvre = normaliserCreation({ item, creation });
            grid.appendChild(creerCarte(
                { url_apercu: creation.url_apercu, url: creation.url },
                () => finaliser(oeuvre),
                creation.style_nom || creation.style
            ));
        });
        grille.appendChild(grid);
    }

    /**
     * Carte vignette cliquable (image chargée en blob via la session).
     * @param {object} data    { url_apercu, url }
     * @param {function} onClick Action au clic
     * @param {string} [libelle] Étiquette affichée sur la vignette
     */
    function creerCarte(data, onClick, libelle) {
        const carte = document.createElement('button');
        carte.type = 'button';
        carte.className = 'tlsd-carte';
        carte.setAttribute('aria-label', libelle || 'Image');

        const img = document.createElement('img');
        img.alt = libelle || 'Image';
        img.classList.add('tls-chargement');
        Toilesia.api.chargerImage(data.url_apercu || data.url)
            .then((objUrl) => {
                img.src = objUrl;
                img.classList.remove('tls-chargement');
            })
            .catch(() => img.classList.add('tls-erreur-img'));

        carte.appendChild(img);

        if (libelle) {
            const info = document.createElement('span');
            info.className = 'tlsd-carte-info';
            info.textContent = libelle;
            carte.appendChild(info);
        }

        carte.addEventListener('click', onClick);
        return carte;
    }

    // ============================================================
    // UPLOAD + GÉNÉRATION
    // ============================================================

    /** Champ d'upload en partie haute du tiroir. */
    function initialiserUpload() {
        const zone = _shadow.querySelector('[data-role="upload"]');
        Toilesia.upload.afficher(zone, (imageId) => {
            demanderConsentement(imageId);
        });
    }

    function montrerVueProgression() {
        // Le suivi de la génération s'affiche PAR-DESSUS la vue
        // « upload + historique » (celle-ci reste en place dessous).
        _shadow.querySelector('.tlsd-overlay').classList.add('tlsd-visible');
    }

    function montrerVueCorps() {
        const overlay = _shadow.querySelector('.tlsd-overlay');
        overlay.classList.remove('tlsd-visible');
        overlay.innerHTML = '';
    }

    /**
     * Résout le style de la page À LA DEMANDE (et rafraîchit _style).
     * Évite qu'un style défini APRÈS l'initialisation (attribut data-style
     * injecté tardivement, Toilesia.config.style renseigné plus tard, ou
     * double appel de init() avec un style) ne soit ignoré : le
     * consentement et la génération relisent toujours la source la plus
     * à jour avant d'agir.
     * @returns {string}
     */
    function resoudreStyle() {
        const conteneur = document.querySelector('[data-toilesia-produit]');
        if (conteneur && conteneur.getAttribute('data-style')) {
            _style = conteneur.getAttribute('data-style');
        }
        if (!_style && Toilesia.config && Toilesia.config.style) {
            _style = Toilesia.config.style;
        }
        return _style;
    }

    /**
     * Écran de consentement (CGU + publication) affiché dans l'overlay
     * AVANT de lancer la génération. La génération ne démarre qu'au clic
     * sur « Générer mon œuvre » (bouton désactivé tant que la CGU n'est
     * pas cochée). Les consentements sont transmis à image/generate.
     */
    function demanderConsentement(imageId) {
        if (!resoudreStyle()) {
            if (Toilesia.notifier) Toilesia.notifier.erreur('Aucun style défini pour cette page produit.');
            return;
        }

        const conteneur = _shadow.querySelector('.tlsd-overlay');
        montrerVueProgression();
        conteneur.innerHTML = '';

        const bloc = document.createElement('div');
        bloc.className = 'tlsd-consentement';

        bloc.innerHTML =
            '<h4 class="tlsd-consentement-titre">Avant de générer</h4>' +
            '<label class="tlsd-consentement-ligne">' +
            '  <input type="checkbox" data-consent="cgu">' +
            '  <span>J\'accepte les Conditions Générales d\'Utilisation</span>' +
            '</label>' +
            '<label class="tlsd-consentement-ligne">' +
            '  <input type="checkbox" data-consent="galerie">' +
            '  <span>J\'accepte que ma création soit publiée dans la galerie publique</span>' +
            '</label>' +
            '<label class="tlsd-consentement-ligne">' +
            '  <input type="checkbox" data-consent="reseaux">' +
            '  <span>J\'accepte que ma création soit publiée sur les réseaux sociaux</span>' +
            '</label>' +
            '<div class="tlsd-consentement-actions">' +
            '  <button type="button" class="tls-btn tls-btn-principal" data-role="generer" disabled>Générer mon œuvre</button>' +
            '  <button type="button" class="tls-btn tls-btn-secondaire" data-role="annuler">Annuler</button>' +
            '</div>';

        conteneur.appendChild(bloc);

        const cgu = bloc.querySelector('[data-consent="cgu"]');
        const btnGenerer = bloc.querySelector('[data-role="generer"]');

        cgu.addEventListener('change', () => {
            btnGenerer.disabled = !cgu.checked;
        });

        btnGenerer.addEventListener('click', () => {
            if (!cgu.checked) return;
            lancerGeneration(imageId, {
                cgu_acceptees: 1,
                consentement_galerie: bloc.querySelector('[data-consent="galerie"]').checked ? 1 : 0,
                consentement_reseaux: bloc.querySelector('[data-consent="reseaux"]').checked ? 1 : 0
            });
        });

        bloc.querySelector('[data-role="annuler"]').addEventListener('click', () => {
            montrerVueCorps();
            chargerOnglet('photos');
        });
    }

    async function lancerGeneration(imageId, consentements) {
        if (!resoudreStyle()) {
            if (Toilesia.notifier) Toilesia.notifier.erreur('Aucun style défini pour cette page produit.');
            return;
        }

        consentements = consentements || {};

        montrerVueProgression();
        const conteneur = _shadow.querySelector('.tlsd-overlay');
        conteneur.innerHTML = '';
        let controleur = null;
        try {
            controleur = Toilesia.progress.afficher(conteneur, { uneEtape: true });
        } catch (e) {
            console.error('[ToilesiaProduit] Erreur affichage progression :', e);
        }

        try {
            const reponse = await Toilesia.api.appeler('image/generate', 'POST', {
                image_id: String(imageId),
                style: _style,
                format: Toilesia.config.formatGeneration,
                cgu_acceptees: consentements.cgu_acceptees ? 1 : 0,
                consentement_galerie: consentements.consentement_galerie ? 1 : 0,
                consentement_reseaux: consentements.consentement_reseaux ? 1 : 0
            }, { silencieux: true });

            if (reponse.succes) {
                if (controleur) controleur.terminerSucces();
                if (Toilesia.notifier) Toilesia.notifier.succes('Votre œuvre est prête !');
                finaliser({
                    creationId: reponse.creation_id,
                    imageId: reponse.image_id,
                    style: reponse.style,
                    format: reponse.format,
                    urlImage: reponse.url_image,
                    urlApercu: reponse.url_apercu,
                    urlApercuPublic: reponse.url_apercu_public || ''
                });
            } else {
                // 409 (style déjà généré pour cette photo) ou échec (502,
                // erreur réseau après un timeout — la génération est
                // synchrone et peut durer ~2 min côté serveur). Dans les
                // deux cas, l'œuvre a pu être enregistrée côté serveur :
                // on la récupère avant d'afficher un échec.
                await finaliserOuEchec(
                    imageId,
                    controleur,
                    reponse.statut_http === 409
                        ? null
                        : (reponse.erreur || 'La génération a échoué.')
                );
            }
        } catch (e) {
            console.error('[ToilesiaProduit] Erreur pendant la génération :', e);
            // Même logique qu'un échec HTTP : la requête a pu aboutir côté
            // serveur avant la perte de connexion (timeout).
            await finaliserOuEchec(imageId, controleur, null);
        }
    }

    /**
     * Après un échec de génération, vérifie si l'œuvre a malgré tout été
     * créée côté serveur (timeout, 409, 502…), puis la réutilise ; sinon
     * affiche l'écran d'échec.
     * @param {*} imageId Identifiant de l'image source
     * @param {object|null} controleur Contrôleur de progression
     * @param {string|null} messageErreur Message d'avertissement (null si 409)
     */
    async function finaliserOuEchec(imageId, controleur, messageErreur) {
        if (controleur) controleur.terminerEchec();

        // Filet de sécurité : si la consultation de l'historique échoue
        // (session perdue, réseau coupé), on retombe sur l'écran d'échec
        // plutôt que de laisser une promesse non gérée.
        let oeuvre = null;
        try {
            oeuvre = await trouverCreationExistante(imageId, _style);
        } catch (e) {
            console.error('[ToilesiaProduit] Vérification historique impossible :', e);
        }

        if (oeuvre) {
            if (Toilesia.notifier) Toilesia.notifier.info('Cette œuvre existait déjà — elle a été réutilisée.');
            finaliser(oeuvre);
        } else {
            if (messageErreur && Toilesia.notifier) {
                Toilesia.notifier.avertissement(messageErreur);
            }
            afficherEchecGeneration();
        }
    }

    /** Retrouve la création existante (image_id, style) dans l'historique. */
    async function trouverCreationExistante(imageId, style) {
        const reponse = await Toilesia.api.appeler('session/history', 'GET', null, { silencieux: true });
        if (!reponse.succes || !Array.isArray(reponse.historique)) return null;
        for (const item of reponse.historique) {
            if (String(item.image_id) !== String(imageId)) continue;
            const creation = (item.creations || []).find((c) => c.style === style);
            if (creation) {
                return {
                    creationId: creation.id,
                    imageId: item.image_id,
                    style: creation.style,
                    format: creation.format,
                    urlImage: creation.url,
                    urlApercu: creation.url_apercu,
                    urlApercuPublic: creation.url_apercu_public || ''
                };
            }
        }
        return null;
    }

    /**
     * Normalise une création issue de l'historique (« Mes œuvres ») vers
     * l'objet interne attendu par finaliser()/injecterProprietes().
     */
    function normaliserCreation({ item, creation }) {
        return {
            creationId: creation.id,
            imageId: (item && item.image_id) || creation.image_id,
            style: creation.style,
            format: creation.format,
            urlImage: creation.url,
            urlApercu: creation.url_apercu,
            urlApercuPublic: creation.url_apercu_public || ''
        };
    }

    function afficherEchecGeneration() {
        const conteneur = _shadow.querySelector('.tlsd-overlay');
        conteneur.innerHTML = '';

        const bloc = document.createElement('div');
        bloc.className = 'tls-erreur-bloc';

        const titre = document.createElement('h4');
        titre.textContent = 'La génération a échoué';
        const texte = document.createElement('p');
        texte.textContent = 'Votre photo reste disponible. Vous pouvez réessayer ou choisir une œuvre existante.';

        const btnReessayer = document.createElement('button');
        btnReessayer.type = 'button';
        btnReessayer.className = 'tls-btn tls-btn-principal';
        btnReessayer.textContent = 'Réessayer';
        btnReessayer.addEventListener('click', () => {
            montrerVueCorps();
            chargerOnglet('photos');
        });

        const btnRetour = document.createElement('button');
        btnRetour.type = 'button';
        btnRetour.className = 'tls-btn tls-btn-secondaire';
        btnRetour.textContent = '← Retour au choix';
        btnRetour.addEventListener('click', () => {
            montrerVueCorps();
            chargerOnglet('oeuvres');
        });

        bloc.appendChild(titre);
        bloc.appendChild(texte);
        bloc.appendChild(btnReessayer);
        bloc.appendChild(btnRetour);
        conteneur.appendChild(bloc);
    }

    function nomStyle(code) {
        const s = (Toilesia.config.styles || []).find((x) => x.code === code);
        if (s) return s.nom;
        const np = Toilesia.config.styleNumberPaint;
        if (np && np.code === code) return np.nom;
        return code;
    }

    // ------------------------------------------------------------
    // FINALISATION
    // ------------------------------------------------------------

    /**
     * Termine la personnalisation : intègre l'image à la galerie du
     * produit, injecte les propriétés dans le formulaire, ferme le
     * tiroir. Les boutons « Ajouter au panier » (commander l'œuvre) et
     * « Personnaliser » (choisir une autre image) restent disponibles.
     */
    function finaliser(creation) {
        _dernierCreation = creation;

        // 1. Image dans la galerie du produit (+ persistance au
        //    changement de variante : voir memoriserOeuvre)
        Toilesia.api.chargerImage(creation.urlApercu || creation.urlImage || creation.url)
            .then((objUrl) => {
                integrerImageProduit(objUrl);
                memoriserOeuvre(objUrl);
            })
            .catch(() => { /* la galerie reste inchangée en cas d'échec */ });

        // 2. Propriétés injectées dans le formulaire
        injecterProprietes(creation);

        // 3. Fermeture du tiroir
        fermerDrawer();

        // 4. « Ajouter au panier » réapparaît ET « Personnaliser »
        //    est conservé (changer d'image / repasser commande)
        maintenirBoutonsApresPersonnalisation();

        if (Toilesia.notifier) {
            Toilesia.notifier.succes('Votre produit est prêt : cliquez sur « Ajouter au panier ».');
        }
    }

    /**
     * Intègre l'image personnalisée dans la galerie du produit.
     * ⚠️ Deux structures de galerie sont gérées :
     *   A) Carrousel « moderne » (thèmes type Origin/Spotlight) :
     *        <media-gallery>
     *          <slideshow-component><slideshow-slides>
     *            <slideshow-slide class="product-media-container ...">
     *              <div class="product-media" data-media-id="...">
     *                <img class="product-media__image" ...>
     *      → l'œuvre remplace l'image de la diapositive actuellement
     *        visible (celle SANS aria-hidden="true"). Aucune
     *        diapositive supplémentaire : le compteur/flèches du
     *        thème restent cohérents.
     *   B) Galerie « classique » (Dawn…) : liste SEL_GALERIE_MAIN.
     */
    const SEL_GALERIE_MAIN = [
        '[data-main-image]',
        '.product__media img',
        '[id*="FeaturedMedia"] img',
        '[id*="MediaGallery"] img',
        '.product-single__media img',
        '#ProductPhoto',
        '#ProductPhoto img',
        '.product-single__photos img',
        '[data-image-zoom]',
        '.main-product__media img'
    ];
    const SEL_GALERIE_THUMBS = '[data-thumbnails], .thumbnail-list, .product__media-list, .thumbnails, .product-single__thumbnails';

    /**
     * Positionne l'URL d'une <img> (fondu au chargement).
     * @param {HTMLImageElement} img
     * @param {string} objUrl URL blob de l'œuvre
     * @returns {boolean} true si l'image a été mise à jour
     */
    function afficherImageElement(img, objUrl) {
        if (!img) return false;

        // Repère visuel pendant le chargement : l'œuvre est chargée en BLOB
        // depuis l'API, donc invisible plusieurs secondes sur une connexion
        // lente — sans repère, la galerie paraît vide (bug « il manque une
        // image de chargement »).
        img.classList.add('tls-p-chargement');
        const terminerChargement = () => {
            img.style.opacity = '1';
            img.classList.remove('tls-p-chargement');
        };

        img.src = objUrl;
        img.removeAttribute('srcset');
        img.style.opacity = '0';
        img.onload = terminerChargement;
        img.onerror = () => img.classList.remove('tls-p-chargement');
        if (img.complete) terminerChargement();
        return true;
    }

    /**
     * Affiche l'œuvre dans un carrousel <media-gallery>/<slideshow-slide>
     * en remplaçant l'image de la diapositive ACTUELLEMENT VISIBLE.
     * Aucune diapositive n'est ajoutée : le compteur et les flèches du
     * thème restent cohérents.
     * @param {string} objUrl URL blob de l'œuvre
     * @returns {boolean} true si une image a été mise à jour
     */
    function afficherImageDansCarrousel(objUrl) {
        const galerie = document.querySelector('media-gallery, [data-media-gallery]');
        if (!galerie) return false;

        const piste = galerie.querySelector('slideshow-slides');
        if (!piste) return false;

        const diapositives = Array.from(piste.querySelectorAll(':scope > slideshow-slide'));
        if (!diapositives.length) return false;

        // Diapositive visible (aria-hidden != "true"), sinon la première
        const diapo = diapositives.find((d) => d.getAttribute('aria-hidden') !== 'true') || diapositives[0];
        const img = diapo.querySelector('.product-media__image, .product-media img, img');

        return afficherImageElement(img, objUrl);
    }

    function integrerImageProduit(objUrl) {
        // 1) Carrousel moderne (thèmes type Origin/Spotlight) : on remplace
        //    l'image de la diapositive visible — prioritaire
        if (afficherImageDansCarrousel(objUrl)) return;

        // 2) Galerie « classique » (Dawn…) : on cherche l'image principale
        let imgPrincipale = null;
        for (const sel of SEL_GALERIE_MAIN) {
            const el = document.querySelector(sel);
            if (!el) continue;
            // Le sélecteur peut viser une <img> directe ou un conteneur
            imgPrincipale = (el.tagName === 'IMG') ? el : el.querySelector('img');
            if (imgPrincipale) break;
        }
        if (imgPrincipale) afficherImageElement(imgPrincipale, objUrl);

        // 3) Miniature ajoutée en tête de la liste (si la galerie en a une)
        const conteneurMiniatures = document.querySelector(SEL_GALERIE_THUMBS);
        if (conteneurMiniatures) {
            const vignette = document.createElement('img');
            vignette.src = objUrl;
            vignette.alt = 'Votre œuvre personnalisée';
            vignette.style.cssText =
                'width:64px;height:80px;object-fit:cover;border-radius:6px;' +
                'border:2px solid var(--tls-accent);cursor:pointer;';
            vignette.addEventListener('click', () => {
                if (imgPrincipale) {
                    imgPrincipale.src = objUrl;
                    imgPrincipale.style.opacity = '1';
                }
            });
            conteneurMiniatures.prepend(vignette);
        }
    }

    // ------------------------------------------------------------
    // PERSISTANCE APRÈS CHANGEMENT D'OPTION (variante)
    //    Certains thèmes (type Origin/Spotlight) RÉINITIALISENT le
    //    carrousel produit quand on change une option (format,
    //    taille…) : les diapositives sont reconstruites et l'œuvre
    //    personnalisée disparaît. On mémorise l'URL blob et on la
    //    réinjecte automatiquement (MutationObserver + événements de
    //    variante + vérifications différées) dès qu'elle n'est plus
    //    présente dans la galerie. On réinjecte aussi les propriétés
    //    panier si le thème a recréé le formulaire.
    // ------------------------------------------------------------
    let _ancreObservee = null;      // ancêtre observé (détection de remplacement)
    let _changeLie = false;         // écouteurs de variante posés une seule fois
    let _timeoutsReaffichage = [];  // vérifications différées (re-rendu asynchrone)

    function memoriserOeuvre(objUrl) {
        if (!objUrl) return;
        _urlOeuvre = objUrl;
        armerSurveillanceGalerie();
        lierChangementOption();
    }

    function lierChangementOption() {
        if (_changeLie) return;
        _changeLie = true;

        // Filet de sécurité si le thème remplace complètement la
        // section galerie (l'observateur serait alors détaché).
        document.addEventListener('change', (e) => {
            const cible = e.target;
            if (!cible || !cible.closest) return;
            if (!cible.closest(SEL_FORM)) return;
            const type = (cible.tagName || '').toLowerCase();
            if (type === 'select' || type === 'input' || type === 'button') {
                planifierReaffichage();
            }
        }, true);

        // Événements de variante émis par les thèmes Shopify
        ['variant:change', 'variantChange', 'variant:updated'].forEach((nom) => {
            document.addEventListener(nom, () => planifierReaffichage());
        });
    }

    function armerSurveillanceGalerie() {
        const galerie = document.querySelector('media-gallery, [data-media-gallery]');
        if (!galerie) return;

        // Déjà armé sur un ancêtre encore présent dans le document
        if (_observateurGalerie && _ancreObservee && document.contains(_ancreObservee)) return;

        const ancetre = galerie.closest('.shopify-section, [data-section], section, main') || document.body;
        if (_observateurGalerie) _observateurGalerie.disconnect();

        _observateurGalerie = new MutationObserver(() => planifierReaffichage());
        _observateurGalerie.observe(ancetre, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src']
        });
        _ancreObservee = ancetre;
    }

    function planifierReaffichage() {
        // Passage au prochain frame (après le rendu du thème)
        if (!_rafSurveillance) {
            const rAF = window.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
            _rafSurveillance = rAF(() => {
                _rafSurveillance = 0;
                verifierApresChangement();
            });
        }
        // Vérifications différées : le re-rendu du thème est asynchrone
        _timeoutsReaffichage.forEach((t) => clearTimeout(t));
        _timeoutsReaffichage = [250, 600, 1200, 2500].map((d) => setTimeout(verifierApresChangement, d));
    }

    function verifierApresChangement() {
        reafficherOeuvre();
        reappliqueProprietes();
        reattacherBoutonPersonnaliser();
    }

    /** L'œuvre est-elle encore présente dans une diapositive du carrousel ? */
    function oeuvrePresenteDansGalerie(galerie) {
        const images = galerie.querySelectorAll('slideshow-slide img');
        for (const img of images) {
            if (img.src === _urlOeuvre) return true;
        }
        return false;
    }

    /**
     * Réinjecte l'œuvre dans la diapositive visible si la galerie a été
     * réinitialisée (changement de variante…). Ne fait RIEN si l'œuvre
     * est toujours affichée (navigation manuelle entre les photos).
     */
    function reafficherOeuvre() {
        if (!_urlOeuvre) return;

        armerSurveillanceGalerie(); // ré-arme si la section a été remplacée

        const galerie = document.querySelector('media-gallery, [data-media-gallery]');
        if (!galerie) return;

        // L'œuvre est encore là : on ne touche à rien (on pourrait
        // écraser une autre photo que le visiteur consulte).
        if (oeuvrePresenteDansGalerie(galerie)) return;

        const piste = galerie.querySelector('slideshow-slides');
        if (!piste) return;

        const diapositives = Array.from(piste.querySelectorAll(':scope > slideshow-slide'));
        if (!diapositives.length) return;

        const diapo = diapositives.find((d) => d.getAttribute('aria-hidden') !== 'true') || diapositives[0];
        const img = diapo.querySelector('.product-media__image, .product-media img, img');

        if (img) afficherImageElement(img, _urlOeuvre);
    }

    /** Le formulaire porte-t-il encore les propriétés de l'œuvre ? */
    function formulairePorteOeuvre(formulaire) {
        const champ = formulaire.querySelector(
            'input[name="properties[_creation_id]"], input[name="properties[creation_id]"]'
        );
        const attendu = (_dernierCreation && _dernierCreation.creationId)
            ? String(_dernierCreation.creationId) : '';
        return champ !== null && champ.value === attendu;
    }

    /**
     * Si le thème a recréé le formulaire produit lors du changement de
     * variante, les champs cachés properties[...] ont été perdus : on
     * les réinjecte pour que la commande garde la traçabilité.
     */
    function reappliqueProprietes() {
        if (!_dernierCreation) return;
        const formulaire = document.querySelector(SEL_FORM);
        if (!formulaire || formulairePorteOeuvre(formulaire)) return;
        _form = formulaire;
        injecterProprietes(_dernierCreation);
    }

    /**
     * Injecte les propriétés de traçabilité dans le formulaire produit.
     * Shopify transmet automatiquement les champs properties[...] sur
     * chaque ligne du panier (webhook orders/create côté backend).
     *
     * ⚠️ Nomenclature :
     *   - les propriétés INTERNES commencent par « _ » (masquées côté
     *     client, visibles dans l'admin et envoyées au webhook) ;
     *   - « Votre oeuvre » contient l'URL PUBLIQUE de l'aperçu (lien /
     *     image affichables partout, sans en-tête de session).
     */
    function injecterProprietes(creation) {
        if (!_form) return;
        const proprietes = {
            'properties[Votre oeuvre]': creation.urlApercuPublic || '',
            'properties[_creation_id]': creation.creationId || '',
            'properties[_image_id]': creation.imageId || '',
            'properties[_style]': creation.style || '',
            'properties[_url_image]': creation.urlImage || creation.url || '',
            'properties[_url_apercu]': creation.urlApercu || ''
        };
        Object.keys(proprietes).forEach((nom) => {
            let input = _form.querySelector('input[name="' + nom + '"]');
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = nom;
                _form.appendChild(input);
            }
            input.value = proprietes[nom];
        });
    }

    /**
     * Après une personnalisation réussie : le bouton « Ajouter au
     * panier » du thème redevient visible ET le bouton « Personnaliser »
     * est CONSERVÉ. Le client peut commander l'œuvre affichée ou
     * recliquer « Personnaliser » pour choisir une autre image.
     */
    function maintenirBoutonsApresPersonnalisation() {
        // Une œuvre vient d'être finalisée : on autorise de nouveau la
        // commande et on réaffiche TOUS les boutons d'ajout au panier
        // (principal + barre sticky mobile).
        leverVerrouAjoutPanier();
        definirVisibiliteTousBoutons(true);
        if (_btnPerso) _btnPerso.style.display = '';
        reattacherBoutonPersonnaliser();
    }

    /**
     * Sécurité : si le thème a recréé le formulaire produit (ex.
     * changement de variante), le bouton « Personnaliser » inséré plus
     * tôt a pu être supprimé → on le recrée, et les deux boutons
     * restent affichés côte à côte (ou empilés selon le thème).
     */
    function reattacherBoutonPersonnaliser() {
        if (!_dernierCreation) return; // état initial géré par remplacerBouton()

        const formulaire = document.querySelector(SEL_FORM);
        if (!formulaire) return;

        const btnAjout = formulaire.querySelector(SEL_BTN) ||
            formulaire.querySelector('button[type="submit"]') ||
            document.querySelector(SEL_BTN);
        if (!btnAjout) return;

        _form = formulaire;
        _btnAjout = btnAjout;

        // Notre bouton est déjà dans ce formulaire → simple réaffichage
        if (_btnPerso && _btnPerso.isConnected && formulaire.contains(_btnPerso)) {
            definirVisibiliteTousBoutons(true);
            _btnPerso.style.display = '';
            return;
        }

        // Recréation (formulaire re-rendu par le thème)
        if (_btnPerso && _btnPerso.isConnected) _btnPerso.remove();
        _btnPerso = document.createElement('button');
        _btnPerso.type = 'button';
        _btnPerso.className = _btnAjout.className + ' tls-p-perso';
        _btnPerso.textContent = '🎨 Personnaliser';
        _btnPerso.setAttribute('aria-label', 'Personnaliser le produit avec votre photo');
        _btnPerso.addEventListener('click', ouvrirDrawer, true);
        _btnAjout.insertAdjacentElement('afterend', _btnPerso);

        definirVisibiliteTousBoutons(true);
        _btnPerso.style.display = '';
    }

    // ============================================================
    // SÉCURITÉ DE VENTE — VERROU POSÉ DÈS LE CHARGEMENT DU SCRIPT
    // ============================================================
    // Le bouton « Ajouter au panier » du thème est masqué IMMÉDIATEMENT, sans
    // attendre DOMContentLoaded ni la création de session : sur une connexion
    // lente, le client ne doit jamais pouvoir commander un produit non
    // personnalisé, ni voir le bouton du thème rester affiché quelques
    // secondes avant l'apparition de « Personnaliser ».
    poserVerrouAjoutPanier();

    // ============================================================
    // API PUBLIQUE
    // ============================================================
    return { init: init };
})();

// Auto-initialisation au chargement (page produit uniquement)
(function () {
    function demarrer() {
        window.ToilesiaProduit.init();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', demarrer);
    } else {
        demarrer();
    }
})();
